import { EThemePreference } from "@frontend-shared/types";
import { Check, Moon, Sun } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateUserPreferences } from "@/api/preferences/useUpdateUserPreferences";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/components/ui/theme-provider";
import {
  THEME_LABEL_KEY_BY_PREFERENCE,
  THEME_OPTIONS,
} from "@/constants/theme-options";

import { USER_MENU_ROW_CLASS } from "./user-menu-row";

/**
 * NOTE: NSAI-5822 — the theme picker: the same three answers as Preferences
 * (system, light, dark), so "follow the system" is one tap away rather than
 * only in Settings. It writes through the same mutation the preferences screen
 * uses, so the choice survives a reload and follows the account to another
 * device. The glyph shows the scheme the provider has actually painted
 * (`resolvedScheme`), not the preference: under `system` and `auto` the
 * provider re-resolves on an OS colour-scheme change or at a schedule boundary
 * without the preference changing, and the glyph has to follow what is on
 * screen. The menu marks the stored preference.
 *
 * NOTE: NSAI-6259 — this is a row of the ACCOUNT MENU now, not a button in the
 * top-right corner. The redesign put a theme toggle, a notifications bell and a
 * share button up there next to the project's breadcrumb; the review asked for
 * that corner back, holding the project selector and little else. So the
 * picker became a flyout, taking the slot the project selector vacated when it
 * moved the other way. The `DropdownMenu` wrapper is gone with the standalone
 * trigger — this renders INSIDE `UserMenu`'s content and inherits its menu.
 */
const ThemePickerComponent = (): JSX.Element => {
  const { t, i18n } = useTranslation(["common", "preferences"]);
  const { theme, setTheme, resolvedScheme } = useTheme();
  const { mutate: updatePreferences } = useUpdateUserPreferences({
    i18n,
    theme,
    setTheme,
  });

  const handleSelect = useCallback(
    (value: EThemePreference): void => {
      if (value !== theme) {
        updatePreferences({ theme: value });
      }
    },
    [theme, updatePreferences],
  );

  const isDark = resolvedScheme === EThemePreference.DARK;
  // NOTE: Named from the STORED preference rather than from the offered list:
  // an account that saved the retired `auto` still reads "Auto" here, instead
  // of being mislabelled as the first offerable option.
  const label = `${t("preferences:theme.title")}: ${t(
    `common:theme.${THEME_LABEL_KEY_BY_PREFERENCE[theme]}`,
  )}`;

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        id="theme-menu-item"
        data-testid="user-menu-theme"
        data-scheme={resolvedScheme}
        data-theme={theme}
        aria-label={label}
        className={USER_MENU_ROW_CLASS}
      >
        {isDark ? (
          <Moon className="h-4 w-4 shrink-0" aria-hidden="true" />
        ) : (
          <Sun className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <span className="truncate">{t("preferences:theme.title")}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        className="min-w-[10rem] p-0"
        data-testid="user-menu-theme-menu"
      >
        {THEME_OPTIONS.map(({ value, key, icon: Icon }) => {
          const isSelected = value === theme;

          return (
            <DropdownMenuItem
              key={value}
              // NOTE: NSAI-6259 — `py-2` on a three-row flyout left it taller
              // than the menu it hangs off. `DropdownMenuItem` already floors
              // the row at `min-h-8` (and at 44px for coarse pointers), so
              // trimming the padding tightens the list without shrinking the
              // touch target.
              className="flex items-center gap-2 py-1 px-4 rounded-none"
              data-testid={`user-menu-theme-option-${key}`}
              data-selected={isSelected}
              onSelect={() => handleSelect(value)}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span className="flex-1">{t(`common:theme.${key}`)}</span>
              {isSelected && (
                <Check className="h-4 w-4 text-foreground" aria-hidden="true" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

export const ThemePicker = memo(ThemePickerComponent);
